// src/pages/moderation/Reports.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const ENDPOINTS = {
    listReports:
        "https://oppiesfunc.azurewebsites.net/api/listReports?code=i0pkwHSOv1SitEE9rzA_ZVaWovyw3eT5xwfSYUTm9gqyAzFuKW3r7Q==",
    updateStatus:
        "https://oppiesfunc.azurewebsites.net/api/updateReportStatus?code=qxF54cy4MUeiF-UQI7cDoQNbuBBNre31iRvwX9pErECsAzFunokr1Q==",
};

const TABS = [
    { key: "NEW", label: "New" },
    { key: "IN_REVIEW", label: "In Review" },
    { key: "RESOLVED", label: "Resolved" },
    { key: "REJECTED", label: "Rejected" },
];

export default function Reports() {
    const [active, setActive] = useState("NEW");
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [sel, setSel] = useState(null);
    const [saving, setSaving] = useState(false);

    const buildUrl = () => {
        const url = new URL(ENDPOINTS.listReports);
        url.searchParams.set("status", active);
        return url.toString();
    };

    const fetchReports = async () => {
        try {
            setError("");
            setLoading(true);
            const res = await axios.get(buildUrl(), { timeout: 20000 });
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error("[Reports] fetch error:", e?.message || e);
            setRows([]);
            setError("Failed to load reports.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReports(); }, []);
    useEffect(() => { fetchReports(); }, [active]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const hay = [
                r._display?.subjectLabel,
                r.reasonCode,
                r._display?.authorName,
                r._display?.authorEmail,
                r._display?.entityLabel,
                r.subjectId,
                r.postId,
                r.description,
                r.assignee,
                r.status,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        });
    }, [rows, search]);

    const saveStatus = async (id, postId, status, assignee) => {
        try {
            setSaving(true);
            await axios.post(ENDPOINTS.updateStatus, { id, postId, status, assignee });
            setRows((prev) => prev.map((x) => (x.id === id ? { ...x, status, assignee } : x)));
            if (sel && sel.id === id) setSel({ ...sel, status, assignee });
            window.dispatchEvent(new CustomEvent("reports:changed"));
        } catch (e) {
            console.error("[Reports] update error:", e?.response?.data || e?.message || e);
            alert(`Failed to update status${e?.response?.data?.error ? `: ${e.response.data.error}` : ""}`);
        } finally {
            setSaving(false);
        }
    };

    const subjectLabel = (r) =>
        r?._display?.subjectLabel ||
        r?.subjectSnapshot?.postPreview?.text ||
        r?.subjectSnapshot?.comment?.text ||
        `${r.subjectType} ${String(r.subjectId).slice(0, 10)}…`;

    const authorLabel = (r) =>
        r?._display?.authorName ||
        r?.subjectSnapshot?.author?.name ||
        r?.reporter?.name ||
        "Unknown";

    const authorEmail = (r) =>
        r?._display?.authorEmail ||
        r?.subjectSnapshot?.author?.email ||
        r?.reporter?.email ||
        "";

    const entityLabel = (r) =>
        r?._display?.entityLabel ||
        r?.subjectSnapshot?.entity?.storeName ||
        r?.subjectSnapshot?.entity?.shopName ||
        r?.entity?.storeId ||
        r?.entity?.shopId ||
        "—";

    return (
        <div style={S.page}>
            <h2 style={S.h2}>Reports</h2>

            <div style={S.toolbar}>
                <div style={S.tabs}>
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setActive(t.key)}
                            style={{ ...S.tab, ...(active === t.key ? S.tabActive : {}) }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <input
                    style={S.search}
                    placeholder="Search subject, author, entity, reason…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <button style={S.refresh} onClick={fetchReports} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>

            {error ? <div style={S.err}>{error}</div> : null}

            <div style={S.tableWrap}>
                <table style={S.table}>
                    <thead>
                        <tr>
                            <th style={S.th}>Created</th>
                            <th style={S.th}>Subject</th>
                            <th style={S.th}>Author</th>
                            <th style={S.th}>Entity</th>
                            <th style={S.th}>Reason</th>
                            <th style={S.th}>Status</th>
                            <th style={S.th}>Assignee</th>
                            <th style={S.th}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={8} style={{ padding: 18, textAlign: "center", color: "#777" }}>
                                    {loading ? "Loading…" : "No reports"}
                                </td>
                            </tr>
                        ) : (
                            filtered.map((r) => (
                                <tr key={r.id}>
                                    <td style={S.td}>
                                        {new Date(r._display?.createdAt || r.createdAt).toLocaleString()}
                                    </td>
                                    <td style={S.td} title={subjectLabel(r)}>{subjectLabel(r)}</td>
                                    <td style={S.td}>
                                        {authorLabel(r)}{authorEmail(r) ? ` (${authorEmail(r)})` : ""}
                                    </td>
                                    <td style={S.td}>{entityLabel(r)}</td>
                                    <td style={S.td}>{r.reasonCode}</td>
                                    <td style={S.td}>
                                        <select
                                            value={r.status}
                                            onChange={(e) => saveStatus(r.id, r.postId, e.target.value, r.assignee)}
                                        >
                                            {TABS.map((t) => (
                                                <option key={t.key} value={t.key}>{t.key}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td style={S.td}>
                                        <input
                                            defaultValue={r.assignee || ""}
                                            placeholder="Unassigned"
                                            style={{ width: 140 }}
                                            onBlur={(e) => {
                                                if ((r.assignee || "") !== e.target.value) {
                                                    saveStatus(r.id, r.postId, r.status, e.target.value);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") e.currentTarget.blur();
                                            }}
                                        />
                                    </td>
                                    <td style={S.td}>
                                        <button style={S.linkBtn} onClick={() => setSel(r)}>View</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {sel && (
                <div style={S.modalBackdrop} onClick={() => setSel(null)}>
                    <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={S.modalHead}>
                            <h3 style={{ margin: 0 }}>Report {sel.id}</h3>
                            <button style={S.closeBtn} onClick={() => setSel(null)}>✕</button>
                        </div>

                        <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                            {sel._display?.imageUrl ? (
                                <img src={sel._display.imageUrl} alt="Reported" style={S.thumb} />
                            ) : null}

                            <div style={{ flex: 1 }}>
                                <p><strong>Subject:</strong> {subjectLabel(sel)}</p>
                                <p><strong>Author:</strong> {authorLabel(sel)}{authorEmail(sel) ? ` (${authorEmail(sel)})` : ""}</p>
                                <p><strong>Entity:</strong> {entityLabel(sel)}</p>
                                <p><strong>Reason:</strong> {sel.reasonCode}</p>
                                <p><strong>Reporter:</strong> {sel?.reporter?.name || sel?.reporter?.email || "—"}</p>
                                <p><strong>Created:</strong> {new Date(sel._display?.createdAt || sel.createdAt).toLocaleString()}</p>
                                <p><strong>Status:</strong> {sel.status} &nbsp; <strong>Assignee:</strong> {sel.assignee || "Unassigned"}</p>
                                <p style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
                                    {sel?.subjectSnapshot?.postPreview?.text ||
                                        sel?.subjectSnapshot?.comment?.text ||
                                        "(No text captured)"}
                                </p>
                            </div>
                        </div>

                        <div style={S.modalActions}>
                            <button
                                style={S.btn}
                                disabled={saving}
                                onClick={() => saveStatus(sel.id, sel.postId, "IN_REVIEW", sel.assignee)}
                            >
                                Mark In Review
                            </button>
                            <button
                                style={{ ...S.btn, background: "#4CAF50", color: "#fff" }}
                                disabled={saving}
                                onClick={() => saveStatus(sel.id, sel.postId, "RESOLVED", sel.assignee)}
                            >
                                Resolve
                            </button>
                            <button
                                style={{ ...S.btn, background: "#cc0000", color: "#fff" }}
                                disabled={saving}
                                onClick={() => saveStatus(sel.id, sel.postId, "REJECTED", sel.assignee)}
                            >
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { padding: 24, maxWidth: 1200, margin: "0 auto" },
    h2: { marginTop: 0 },
    toolbar: { display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" },
    tabs: { display: "flex", gap: 8, flexWrap: "wrap" },
    tab: { padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", cursor: "pointer" },
    tabActive: { background: "#f8f4ea", borderColor: "#bfa97c", fontWeight: 700 },
    search: { flex: 1, minWidth: 260, padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc" },
    refresh: { padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", cursor: "pointer" },
    err: { background: "#fdecea", color: "#b71c1c", padding: "8px 12px", borderRadius: 8, marginBottom: 10 },
    tableWrap: { overflowX: "auto", borderRadius: 10, border: "1px solid #eee" },
    table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
    th: { textAlign: "left", padding: "10px 12px", background: "#fafafa", borderBottom: "1px solid #eee", position: "sticky", top: 0 },
    td: { padding: "10px 12px", borderBottom: "1px solid #f0f0f0" },
    linkBtn: { background: "transparent", border: "none", color: "#1a73e8", cursor: "pointer", padding: 0 },
    modalBackdrop: {
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 9999
    },
    modal: {
        background: "#fff", borderRadius: 12, padding: 16, width: "100%", maxWidth: 900,
        boxShadow: "0 8px 28px rgba(0,0,0,0.2)"
    },
    modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
    closeBtn: { background: "transparent", border: "none", fontSize: 18, cursor: "pointer" },
    btn: { padding: "8px 12px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", cursor: "pointer" },
    modalActions: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 },
    thumb: { width: 180, height: 180, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" },
};
